import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { DocumentRequirement, DocumentUploadState } from '../../../domain/document-collection';
import { CollectDocumentsNextAction } from '../../../domain/qualification';
import { ErrorType, initialDataLoadState } from '../../../store/reducers/common';
import { continueApplicationProcessing } from '../../application/reducers/application';

const initialState = {
  ...initialDataLoadState,
  requiredDocuments: [] as DocumentUploadState[],
};

const byName = (a: DocumentRequirement, b: DocumentRequirement) => {
  return a.documentName < b.documentName ? -1 : 1;
};

const slice = createSlice({
  name: 'document-requirements',
  initialState,
  reducers: {
    uploadDocuments: (
      state,
      action: PayloadAction<{
        documents: DocumentUploadState[];
        nextAction: CollectDocumentsNextAction;
      }>,
    ) => ({
      ...state,
      loading: true,
      error: null,
      requiredDocuments: action.payload.documents.map((doc) => ({
        ...doc,
        uploading: true,
      })),
    }),
    uploadDocumentsSuccess: (state) => ({
      ...state,
      loading: false,
      error: null,
    }),
    uploadDocumentsFail: (state, { payload: { message } }: PayloadAction<ErrorType>) => ({
      ...state,
      loading: false,
      error: { message },
    }),
    uploadFinancialDocuments: (
      state,
      { payload: { document } }: PayloadAction<{ document: DocumentRequirement; files: File[] }>,
    ) => ({
      ...state,
      loading: true,
      error: null,
      requiredDocuments: state.requiredDocuments.map((doc) =>
        doc.documentName === document.documentName && doc.entityToken === document.entityToken
          ? { ...doc, progress: 0, progressComputable: true, uploading: true }
          : doc,
      ),
    }),
    uploadFinancialDocumentsSuccess: (state) => ({
      ...state,
      loading: false,
      error: null,
    }),
    uploadFinancialDocumentsFail: (state, { payload: { message } }: PayloadAction<ErrorType>) => ({
      ...state,
      loading: false,
      error: { message },
      requiredDocuments: state.requiredDocuments.map((doc) => ({ ...doc, uploading: false })),
    }),
    uploadStatusUpdate: (
      state,
      {
        payload: { documentName, entityToken, progress, progressComputable, uploading },
      }: PayloadAction<Omit<DocumentUploadState, 'file' | 'entityName'>>,
    ) => {
      const updatedDocs = state.requiredDocuments.map((doc) => {
        if (doc.documentName === documentName && doc.entityToken === entityToken) {
          return {
            ...doc,
            progress,
            progressComputable,
            uploading,
          };
        } else {
          return doc;
        }
      });
      return {
        ...state,
        requiredDocuments: updatedDocs,
      };
    },
  },

  extraReducers: (builder) => {
    builder.addCase(
      continueApplicationProcessing,
      (
        state,
        {
          payload: {
            response: { documentRequirements },
          },
        },
      ) => {
        const updated: typeof state = {
          ...state,
          requiredDocuments: (documentRequirements ?? []).sort(byName).map((doc) => ({
            ...doc,
            file: null,
            progress: 0,
            uploading: false,
            progressComputable: true,
          })),
        };
        return updated;
      },
    );
  },
});

export const documentRequirementsReducer = slice.reducer;
export const {
  uploadDocumentsFail,
  uploadDocuments,
  uploadDocumentsSuccess,
  uploadStatusUpdate,
  uploadFinancialDocuments,
  uploadFinancialDocumentsSuccess,
  uploadFinancialDocumentsFail,
} = slice.actions;
